import { EducationLevel } from "src/__generated__/graphql";
import { z } from "zod";

export const tertiaryQualifications = [
	EducationLevel.Ond,
	EducationLevel.Hnd,
	EducationLevel.Bachelors,
	EducationLevel.Masters,
	EducationLevel.Doctorate,
];

const mediaItemSchema = z.object({
	key: z.string(),
	mimeType: z.string(),
});

const educationSchema = z
	.object({
		schoolName: z.string().min(1, "School name is required"),
		fieldOfStudy: z.string().optional(),
		qualification: z.nativeEnum(EducationLevel, {
			errorMap: () => ({ message: "Please select a qualification" }),
		}),
	})
	.refine(
		(data) => {
			if (
				data.qualification &&
				tertiaryQualifications.includes(data.qualification)
			) {
				return !!data.fieldOfStudy && data.fieldOfStudy.trim().length > 0;
			}
			return true;
		},
		{
			message: "Field of study is required for tertiary qualifications",
			path: ["fieldOfStudy"],
		}
	);

const employmentSchema = z.object({
	companyName: z.string().min(1, "Company name is required"),
	jobTitle: z.string().min(1, "Job title is required"),
	description: z.string().min(1, "Job description is required"),
	startedAt: z.string().min(1, "Start date is required"),
	endedAt: z.string().optional(),
	media: z.array(mediaItemSchema).optional(),
});

const projectSchema = z.object({
	name: z.string().min(1, "Project name is required"),
	description: z.string().min(1, "Project description is required"),
	startedAt: z.string().optional(),
	endedAt: z.string().min(1, "End date is required"),
	media: z.array(mediaItemSchema).optional(),
});

const certificationSchema = z.object({
	name: z.string().min(1, "Certification name is required"),
	issuerCertificationId: z.string().optional(),
	issuedBy: z.string().min(1, "Issuer is required"),
	issuedAt: z.string().min(1, "Issue date is required"),
	url: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
});

export const jobApplicationFormSchema = z.object({
	educationItems: z.array(educationSchema),
	employmentHistoryItems: z.array(employmentSchema),
	projectHistoryItems: z.array(projectSchema),
	certificationItems: z.array(certificationSchema),
});

export type JobApplicationFormData = z.infer<typeof jobApplicationFormSchema>;

export interface JobApplicationFormProps {
	onSubmit: (data: any) => void;
	onCancel: () => void;
	isSubmitting: boolean;
	jobId: string;
}

export const educationLevels = [
	{
		value: EducationLevel.Jssce,
		label: "Junior Secondary School Certificate (JSSCE)",
	},
	{
		value: EducationLevel.Ssce,
		label: "Senior School Certificate (e.g WAEC, NECO, GCE)",
	},
	{
		value: EducationLevel.Nabteb,
		label:
			"National Business and Technical Examination Board Certificate (NABTEB)",
	},
	{ value: EducationLevel.Ond, label: "Ordinary National Diploma (OND)" },
	{ value: EducationLevel.Hnd, label: "Higher National Diploma (HND)" },
	{ value: EducationLevel.Bachelors, label: "Bachelor's Degree (BSc)" },
	{ value: EducationLevel.Masters, label: "Master's Degree (MSc)" },
	{ value: EducationLevel.Doctorate, label: "Doctorate Degree (PhD)" },
];
